import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, Redirect } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { PolicyText } from './Info';
import * as ROUTES from '../constants/routes';
import { useFormInput, useRouter, useSignInAuthorization } from '../hooks';
import { signIn, signInWithProvider } from '../actions';
import { getSignInError, getSignInFetching } from '../reducers';
import Icon from './Icon';

function useRedirectUrl() {
  const { location } = useRouter();
  const query = location.search.split('=');
  if (query.length === 2 && query[0] === '?redirect') {
    return query[1];
  }
  return ROUTES.VOTE;
}

function SignInSocial({ provider, children }) {
  const dispatch = useDispatch();
  async function handleClick(event) {
    event.preventDefault();
    await dispatch(signInWithProvider(provider));
  }

  const isSignedIn = useSignInAuthorization(false);
  const redirectUrl = useRedirectUrl();
  if (isSignedIn) {
    return <Redirect to={redirectUrl} />;
  }

  return (
    <button className={`btn btn-${provider}`} onClick={handleClick}>
      <Icon icon={['fab', provider]} fixedWidth />
      {children}
    </button>
  );
}

export function SocialSignInButtons() {
  const { t } = useTranslation();

  return (
    <>
      <SignInSocial provider="google">
        {t('auth.signInWithGoogle', 'Sign in with Google')}
      </SignInSocial>
      <SignInSocial provider="facebook">
        {t('auth.signInWithFacebook', 'Sign in with Facebook')}
      </SignInSocial>
      <SignInSocial provider="x-twitter">
        {t('auth.signInWithX', 'Sign in with X')}
      </SignInSocial>
    </>
  );
}

export default function SignIn() {
  const email = useFormInput('');
  const password = useFormInput('');
  const { t } = useTranslation();

  const error = useSelector(getSignInError);
  const errorMessage =
    error &&
    (error.code === 'auth/too-many-requests'
      ? t(
          'auth.errorTooManyAttempts',
          'Too many unsuccessful login attempts. Please try again later'
        )
      : error.message);
  const isFetching = useSelector(getSignInFetching);

  const dispatch = useDispatch();
  function handleSubmit(event) {
    event.preventDefault();
    dispatch(signIn(email.value, password.value));
  }

  const isSignedIn = useSignInAuthorization(false);
  const redirectUrl = useRedirectUrl();
  if (isSignedIn) {
    return <Redirect to={redirectUrl} />;
  }

  const isInvalid = password.value === '' || email.value === '';

  return (
    <div className="container">
      <form className="form-auth" onSubmit={handleSubmit} autoComplete="off">
        <div className="text-center mb-4">
          <h1 className="h3 mb-3 font-weight-normal">
            {t('auth.signInTitle', 'Sign In')}
          </h1>
        </div>
        <div className="form-label-group">
          <input
            type="email"
            className="form-control"
            placeholder={t('auth.emailPlaceholder', 'Email address')}
            required
            autoFocus
            autoCapitalize="off"
            autoCorrect="off"
            name="email"
            {...email}
          />
          <label htmlFor="inputEmail">
            {t('auth.emailPlaceholder', 'Email address')}
          </label>
        </div>
        <div className="form-label-group">
          <input
            type="password"
            className="form-control"
            placeholder={t('auth.passwordPlaceholder', 'Password')}
            required
            name="password"
            {...password}
          />
          <label htmlFor="inputPassword">
            {t('auth.passwordPlaceholder', 'Password')}
          </label>
        </div>
        <button
          disabled={isInvalid}
          className="btn btn-primary btn-block"
          type="submit"
        >
          {isFetching ? (
            <>
              <span
                className="spinner-grow spinner-grow-sm"
                role="status"
                aria-hidden="true"
              ></span>
              <span className="sr-only">{t('common.loading')}</span>
            </>
          ) : (
            t('auth.signInButton', 'Sign in')
          )}
        </button>
        <div className="text-center">
          <div className="mt-2">
            <Link to={ROUTES.PASSWORD_FORGET}>
              {t('auth.forgetPassword', 'Forget password?')}
            </Link>
          </div>
          {!isFetching && error && (
            <p className="mt-2 text-danger">{errorMessage}</p>
          )}
          <div className="text-center my-3">{t('auth.orDivider', '-or-')}</div>
          <SocialSignInButtons />
          <PolicyText />
        </div>
      </form>
    </div>
  );
}
